import { useEffect, useRef } from "react";
import { useChatStore } from "../../../../state/chat";
import { useIsMobile } from "../../../../utils/useIsMobile";
import getThemeColors from "../../../../utils/getColorTheme";
import { useTheme } from "../../../../contexts/ThemeContext";

interface InputSectionProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  quickOptions?: string[];
  handleQuickOptionClick?: (option: string) => void;
  showInput?: boolean;
  disabled?: boolean;
  selectedChat?: any;
  selectedChatOption?: string;
}

const InputSection: React.FC<InputSectionProps> = ({
  value,
  onChange,
  onSubmit,
  quickOptions = [],
  handleQuickOptionClick = () => {}, 
  showInput = true, 
  disabled = false,
  selectedChat,
  selectedChatOption,
}) => {
  const { isReceivingMessage } = useChatStore();
  const { addTheme } = useTheme();
  const { theme } = getThemeColors(addTheme);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isReceivingMessage && !isMobile) {
      textAreaRef.current?.focus();
    }
  }, [isReceivingMessage]);


  useEffect(() => {
    const textArea = textAreaRef.current;
    const adjustHeight = () => {
      if (textArea) {
        textArea.style.height = "25px";
        const maxHeight = isMobile ? 300 : 400;
        textArea.style.height = Math.min(textArea.scrollHeight, maxHeight) + "px";
      }
    };

    adjustHeight();
    textArea?.addEventListener("input", adjustHeight);

    return () => {
      textArea?.removeEventListener("input", adjustHeight);
    };
  }, [value, showInput]);

  return (
    <div className="w-100 d-flex align-items-center gap-3">
      {quickOptions.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "start" }}>
          {quickOptions.map((option) => (
            <button
              key={option}
              onClick={() => handleQuickOptionClick(option)}
              aria-label={`Choose option: ${option}`}
              style={{
                backgroundColor: "#16140e",
                color: "white",
                padding: "8px 16px",
                borderRadius: "20px",
                fontSize: "14px",
                cursor: "pointer",
                border: "none",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#f9c550";
                e.currentTarget.style.color = "#16140e";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#16140e";
                e.currentTarget.style.color = "white";
              }}
            >
              {option}
            </button>
          ))}
        </div>
      ) : showInput ? (
        <textarea
          ref={textAreaRef}
          className="w-100 d-flex align-items-center overflow-auto textarea-send-message"
          style={{
            padding: "10px 10px",
            borderWidth: 0,
            background: "transparent",
            color: theme,
            resize: "none",
            lineHeight: "25px",
            display: "flex",
            alignItems: "center"
          }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isMobile ? "Ask a question" : "Ask me a question to get started."}
          autoComplete="off"
          disabled={disabled}
          onKeyDown={(evt) => {
            if (evt.key === "Enter" && !evt.shiftKey && value.trim()) {
              evt.preventDefault();
              onSubmit();
            }
          }}
        />
      ) : null}
    </div>
  );
};

export default InputSection;
